import { useQuery } from '@tanstack/react-query';
import { contentTestQuery } from '@/entities/content-test';

const POLL_INTERVAL_MS = 2000;

export interface UseContentTestPollingOptions {
  /**
   * Gate the fetch. Used to hold polling until deep-link resolution settles;
   * when omitted (the default) the fetch runs as soon as `testId` is present.
   */
  enabled?: boolean;
  /**
   * Suppress the global 404 toast. The deep-link seam owns the not-found UX
   * while its flag is on; every other caller keeps the pre-seam toast.
   */
  suppressErrorNotification?: boolean;
}

// Poll unless the test has reached a terminal status. Inverting the guard
// (vs. polling only when `running`) keeps us polling across intermediate
// states like `draft` while the run is being started, and accommodates any
// future intermediate status the server may add without breaking the page.
export const useContentTestPolling = (
  testId: string | null | undefined,
  options: UseContentTestPollingOptions = {},
) =>
  useQuery({
    ...contentTestQuery(testId, { suppressErrorNotification: options.suppressErrorNotification }),
    // Only override the base `enabled: !!testId` gate when the caller asks to
    // hold the fetch, so existing call sites keep today's behavior untouched.
    ...(options.enabled === undefined ? {} : { enabled: !!testId && options.enabled }),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed') return false;
      return POLL_INTERVAL_MS;
    },
  });
